import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { queryBuilder } from '@metromarkets/sdk';
import { map, switchMap } from 'rxjs/operators';
import { deserialize } from 'serialize-ts';
import { HttpClient, HttpParams } from '@angular/common/http';
import { QueryParamsEncoder } from '~shared/helpers/query-encoder';
import { Filters } from '../models';
import { API_FILTERS_ROUTE_V3 } from '../constants';
import { QUERY_OPTIONS_KEYS, QueryOptions } from '~shared/models';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { EXPERIMENT_VARIATIONS, PRODUCT_TYPES_EXPERIMENT_KEY } from '~shared/constants';
import { SearchVersionsEnum } from '~app/modules/catalog/services/search-versions.enum';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Injectable({
  providedIn: 'root',
})
export class FiltersHttpService {
  constructor(
    private http: HttpClient,
    private queryParamsEncoder: QueryParamsEncoder,
    @Inject(PLATFORM_ID) private platformId: Object,
    private optimizelyService: OptimizelyService,
    private productsListUrlService: ProductsListUrlService
  ) {}

  getFilters(
    filter: { [key: string]: any },
    queryParams: QueryOptions,
    isCategoryPage: boolean,
    userRegion?: string
  ): Observable<Filters> {
    if (!this.productsListUrlService.isSearchPage()) {
      return this.getRequest(this.getHTTPParams(filter, queryParams, isCategoryPage, null, userRegion));
    }

    return this.optimizelyService.getExperimentVariant(PRODUCT_TYPES_EXPERIMENT_KEY).pipe(
      switchMap((variant: EXPERIMENT_VARIATIONS) => {
        return this.getRequest(
          this.getHTTPParams(filter, queryParams, isCategoryPage, SearchVersionsEnum[variant], userRegion)
        );
      })
    );
  }
  private getRequest(httpParams: HttpParams): Observable<Filters> {
    return this.http
      .get<Filters>(API_FILTERS_ROUTE_V3(), { params: httpParams })
      .pipe(map((response: Filters) => deserialize(response, Filters)));
  }

  private getHTTPParams(
    filter: { [key: string]: any },
    queryParams: QueryOptions,
    isCategoryPage: boolean,
    version: string = null,
    userRegion?: string
  ): HttpParams {
    const params = queryBuilder.toParams({ filter }) as { [key: string]: string | string[] };
    const extraParams = this.getExtraQueryOptions(queryParams);

    return new HttpParams({
      fromObject: {
        ...(version && { version }),
        ...params,
        ...extraParams,
        ...(isCategoryPage && { withAggregatedCategories: '0' }),
        ...{ useCharacteristics: '1' },
        ...(userRegion && { region: userRegion }),
      },
      encoder: this.queryParamsEncoder,
    });
  }

  private getExtraQueryOptions(queryOptions: QueryOptions = {}): HttpParams {
    const restQuery = { ...queryOptions };

    QUERY_OPTIONS_KEYS.forEach((key) => {
      if (restQuery?.[key]) {
        delete restQuery[key];
      }
    });

    return queryBuilder.toParams({
      ...restQuery,
    }) as HttpParams;
  }
}
